// =====================================================================
// ReportViewerModal (S39) — modal fullscreen com iframe pra ver o HTML
// completo do relatorio. Iframe sandbox impede scripts e mantem isolamento.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import { makeStyles } from "@material-ui/core/styles";

import {
  getDailyReport,
  downloadDailyReport
} from "../../../services/dailyReportsService";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: theme.spacing(1)
  },
  dialogContent: {
    padding: 0,
    height: "85vh"
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    display: "block"
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%"
  },
  failedWrap: {
    padding: theme.spacing(4),
    textAlign: "center"
  }
}));

const WEEKDAYS_PT = [
  "Domingo",
  "Segunda-feira",
  "Terca-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sabado"
];

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T12:00:00Z`);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  const weekday = WEEKDAYS_PT[d.getUTCDay()];
  return `${dd}/${mm}/${yyyy} (${weekday})`;
};

const ReportViewerModal = ({ reportId, reportDate, onClose }) => {
  const classes = useStyles();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDailyReport(reportId)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err.message || "Erro ao carregar");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const handleDownload = () => {
    if (!report) return;
    downloadDailyReport(report.id, `relatorio_${report.report_date}.html`).catch(
      () => {}
    );
  };

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6">
          Relatorio de {formatDate(report?.report_date || reportDate)}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className={classes.failedWrap}>
            <Typography variant="h6" color="error" gutterBottom>
              Erro ao carregar relatorio
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Box>
        ) : report?.generation_status === "failed" ? (
          <Box className={classes.failedWrap}>
            <Typography variant="h6" color="error" gutterBottom>
              Geracao falhou
            </Typography>
            <Typography variant="body2" gutterBottom>
              {report.generation_error || "Erro nao especificado"}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Use o botao "Regenerar" no card pra tentar novamente.
            </Typography>
          </Box>
        ) : (
          <iframe
            srcDoc={report?.html_content || ""}
            title={`Relatorio ${report?.report_date || ""}`}
            className={classes.iframe}
            sandbox="allow-same-origin"
          />
        )}
      </DialogContent>

      <DialogActions>
        {report?.generation_status === "success" && (
          <Button
            startIcon={<GetAppIcon />}
            onClick={handleDownload}
            color="primary"
          >
            Baixar HTML
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportViewerModal;
